import React from 'react';
import { deleteCannedAnswer, } from '@Api';
import { IAbilities, IAuth, IOrganizationStore, } from '@Store';
import { CrudEnum, IPreviewCannedAnswer, PermissionEnum, EnumTranslateTitle, EnumListSearchProps, TCannedAnswerSortableListCol, CannedAnswerChannelEnum, CannedAnswerTypeEnum, } from 'atlas-shared';
import { tableColumnDatetimeSorter, tableColumnBooleanRender, tableColumnStringSorter, timeSince, } from '@Utils';
import { TFunction, } from 'i18next';
import { NavigateFunction, } from 'react-router';
import { ColumnsType, } from 'antd/lib/table/interface';
import { listAuditButton, listCloneButton, listDeleteButton, listEditButton, GetColumnSearchProps, GetColumnSearchLikeProps, GetColumnSearchEqualProps, } from '../components';
import { IWithListProps, } from '@Hocs/with.list';

export const CannedAnswerListColumns = (t: TFunction, navigate: NavigateFunction, showDeleted: boolean, organizations: IOrganizationStore, abilities: IAbilities, auth: IAuth, buttons: Array<(row: IPreviewCannedAnswer) => React.ReactElement> = [], filters: IWithListProps<IPreviewCannedAnswer, TCannedAnswerSortableListCol>['filters'], sorter: IWithListProps<IPreviewCannedAnswer, TCannedAnswerSortableListCol>['sorter']): ColumnsType<IPreviewCannedAnswer> => {
  return [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IPreviewCannedAnswer, b: IPreviewCannedAnswer) => a.id - b.id,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'id')?.order || null,
      filteredValue: filters.id || null,
      ...GetColumnSearchEqualProps(t, 'id', t('ID')),
      className: 'table-cell-id',
    },
    auth.user.__admin_organization_ids.length > 1 && {
      title: t('ORGANIZATION'),
      dataIndex: 'organization_id',
      key: 'organization_id',
      render: (organization_id: IPreviewCannedAnswer['organization_id'], canned_answer: IPreviewCannedAnswer) => organizations.dict?.[organization_id]?.title || t('ORGANIZATION_NOT_FOUND', { replace: { organization_id, }, }),
      sorter: (a: IPreviewCannedAnswer, b: IPreviewCannedAnswer) => (organizations.dict?.[a.organization_id]?.title || '').localeCompare(organizations.dict?.[b.organization_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'organization_id')?.order || null,
      filteredValue: filters.organization_id || null,
      ...GetColumnSearchProps(t, 'organization_id', t('ORGANIZATION'), organizations.organizations),
      className: 'table-cell-organization_id',
    },
    {
      title: t('TITLE'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IPreviewCannedAnswer, b: IPreviewCannedAnswer) => tableColumnStringSorter(a.title, b.title),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'title')?.order || null,
      filteredValue: filters.title || null,
      ...GetColumnSearchLikeProps(t, 'title', t('TITLE')),
      className: 'table-cell-title',
    },
    {
      title: t('CHANNEL'),
      dataIndex: 'channel',
      key: 'channel',
      render: (channel: IPreviewCannedAnswer['channel'], canned_answer: IPreviewCannedAnswer) => EnumTranslateTitle('CANNED_ANSWER_CHANNEL', channel, t),
      sorter: (a: IPreviewCannedAnswer, b: IPreviewCannedAnswer) => EnumTranslateTitle('CANNED_ANSWER_CHANNEL', a.channel, t).localeCompare(EnumTranslateTitle('CANNED_ANSWER_CHANNEL', b.channel, t)),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'channel')?.order || null,
      filteredValue: filters.channel || null,
      ...GetColumnSearchProps(t, 'channel', t('CHANNEL'), EnumListSearchProps('CANNED_ANSWER_CHANNEL', CannedAnswerChannelEnum, t)),
      className: 'table-cell-channel',
    },
    {
      title: t('TYPE'),
      dataIndex: 'type',
      key: 'type',
      render: (type: IPreviewCannedAnswer['type'], canned_answer: IPreviewCannedAnswer) => EnumTranslateTitle('CANNED_ANSWER_TYPE', type, t),
      sorter: (a: IPreviewCannedAnswer, b: IPreviewCannedAnswer) => EnumTranslateTitle('CANNED_ANSWER_TYPE', a.type, t).localeCompare(EnumTranslateTitle('CANNED_ANSWER_TYPE', b.type, t)),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'type')?.order || null,
      filteredValue: filters.type || null,
      ...GetColumnSearchProps(t, 'type', t('TYPE'), EnumListSearchProps('CANNED_ANSWER_TYPE', CannedAnswerTypeEnum, t)),
      className: 'table-cell-type',
    },
    {
      title: t('GLOBAL'),
      dataIndex: 'global',
      key: 'global',
      render: (global: IPreviewCannedAnswer['global'], canned_answer: IPreviewCannedAnswer) => tableColumnBooleanRender(t, global),
      sorter: (a: IPreviewCannedAnswer, b: IPreviewCannedAnswer) => (a.global ? 1 : 0) > (b.global ? 1 : 0) ? 1 : -1,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'global')?.order || null,
      filteredValue: filters.global || null,
      ...GetColumnSearchProps(t, 'global', t('GLOBAL'), [{ id: true, title: t('YES'), }, { id: false, title: t('NO'), }, ]),
      className: 'table-cell-global',
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: IPreviewCannedAnswer['created_at'], canned_answer: IPreviewCannedAnswer) => <div className='date-column'>{created_at ? timeSince(t, new Date(created_at), true) : ''}</div>,
      sorter: (a: IPreviewCannedAnswer, b: IPreviewCannedAnswer) => tableColumnDatetimeSorter(a.created_at, b.created_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'created_at')?.order || null,
      className: 'table-cell-created_at',
    },
    showDeleted && { 
      title: t('DELETED'), 
      dataIndex: 'deleted_at',
      key: 'deleted_at',
      className: 'table-cell-deleted_at',
      render: (deleted_at: IPreviewCannedAnswer['deleted_at']) => deleted_at ? timeSince(t, new Date(deleted_at), true) : '',
      sorter: (a: IPreviewCannedAnswer, b: IPreviewCannedAnswer) => tableColumnDatetimeSorter(a.deleted_at, b.deleted_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'deleted_at')?.order || null,
    },
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: any, row: IPreviewCannedAnswer) => (
        <div className={'table-operation-wrp'}>
          {!row.deleted_at && buttons.map(button => button(row))}
          {abilities.can(row.organization_id, CrudEnum.Insert, PermissionEnum.CannedAnswer, row) && listCloneButton(t, navigate, 'canned_answer', row)}
          {abilities.can(row.organization_id, CrudEnum.Read, PermissionEnum.Audit, row) && listAuditButton(t, navigate, 'canned_answer', row)}
          {!row.deleted_at && abilities.can(row.organization_id, CrudEnum.Update, PermissionEnum.CannedAnswer, row) && listEditButton(t, navigate, 'canned_answer', row)}
          {!row.deleted_at && abilities.can(row.organization_id, CrudEnum.Delete, PermissionEnum.CannedAnswer, row) && listDeleteButton(t, row, deleteCannedAnswer)}
        </div>
      ),
    },
  ].filter(Boolean) as ColumnsType<IPreviewCannedAnswer>;

};
